import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, setDoc, doc, deleteDoc, serverTimestamp, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import SecureInput from '../common/SecureInput';
import { logEntityChanges, logUserAction } from '../../utils/logger';
import { sanitizeString } from '../../utils/sanitization';
import { useAuth } from '../auth/AuthContext';
import { useGamification } from '../gamification/GamificationContext';
import { collectionPath } from '../../utils/firestoreCollections';
import { convertDateForSave } from '../../utils/dateHelpers';
import { validateEntity } from '../../utils/schemaValidation';
import { handleFirestoreError, PerformanceMonitor } from '../../utils/errorHandling';
import { hasPermission, FEATURE_PERMISSIONS } from '../../utils/permissions';
import { getAssignmentUsers } from '../../utils/assignmentUsers';

const BUSINESS_TYPE_OPTIONS = [
    "Country Club",
    "Management Company",
    "Specialty Store",
    "Department Store",
    "Resort",
    "Online",
    "Not Specified"
];

const FOCUS_OPTIONS = [
    "Golf",
    "Tennis",
    "Pickleball",
    "Other"
];

// Helper to convert to yyyy-MM-dd
function toInputDate(val) {
    if (!val) return '';
    // If already in yyyy-MM-dd, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    
    let d;
    // Handle Firestore Timestamp
    if (val.toDate && typeof val.toDate === 'function') {
        d = val.toDate();
    }
    // Handle Firestore Timestamp with seconds property
    else if (val.seconds) {
        d = new Date(val.seconds * 1000);
    }
    // Handle regular Date or string
    else {
        d = new Date(val);
    }
    
    if (!d || isNaN(d)) return '';
    
    // Use UTC methods to avoid timezone shifts
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

const CustomerModal = ({ user, users = [], initial, onClose }) => {
    // Sales rep assignment
    // Find the initial sales rep by matching uid or displayName
    const getInitialSalesRepId = () => {
        if (!initial) return '';
        // Try to match by salesRepId (uid)
        if (initial.salesRepId && users.some(u => u.uid === initial.salesRepId)) {
            return initial.salesRepId;
        }
        // Try to match by salesReps array (displayName)
        if (Array.isArray(initial.salesReps) && initial.salesReps.length > 0) {
            // Find the first matching user by displayName
            const repName = initial.salesReps[0].salesRepName || initial.salesReps[0].displayName;
            if (repName) {
                const match = users.find(u => (u.displayName || '').toLowerCase() === repName.toLowerCase());
                if (match) return match.uid;
            }
        }
        return '';
    };
    const [salesRepId, setSalesRepId] = useState(getInitialSalesRepId());
    const { currentUser } = useAuth();
    const { recordActivity } = useGamification();
    const [name, setName] = useState(initial?.customerName || '');
    const [email, setEmail] = useState(initial?.customerEmail || '');
    const [phone, setPhone] = useState(initial?.customerPhone || '');
    const [address1, setAddress1] = useState(initial?.customerAddress1 || '');
    const [address2, setAddress2] = useState(initial?.customerAddress2 || '');
    const [city, setCity] = useState(initial?.customerCity || '');
    const [stateVal, setStateVal] = useState(initial?.customerState || '');
    const [zip, setZip] = useState(initial?.customerZip || '');
    const [country, setCountry] = useState(initial?.customerCountry || 'US');
    const [status, setStatus] = useState(initial?.customerStatus || 'Lead');
    const [lastModified, setlastModified] = useState(
        toInputDate(initial?.lastModified || '')
    );
    const [website, setWebsite] = useState(initial?.website || '');
    const [businessType, setBusinessType] = useState(
        Array.isArray(initial?.businessType) && initial.businessType.length > 0
            ? initial.businessType[0]
            : (typeof initial?.businessType === 'string' ? initial.businessType : '')
    );
    const [businessTypeOptions, setBusinessTypeOptions] = useState(BUSINESS_TYPE_OPTIONS);
    const [showNewBusinessType, setShowNewBusinessType] = useState(false);
    const [newBusinessType, setNewBusinessType] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [focus, setFocus] = useState(initial?.focus || '');
    const [focusOptions, setFocusOptions] = useState(FOCUS_OPTIONS);
    const [selectedFocus, setSelectedFocus] = useState(
      Array.isArray(initial?.businessFocus)
        ? initial.businessFocus
        : initial?.businessFocus
          ? [initial.businessFocus]
          : []
    );
    const [showNewFocus, setShowNewFocus] = useState(false);
    const [newFocus, setNewFocus] = useState('');
    const [focusFieldError, setFocusFieldError] = useState('');

    // Clear focus field error when user updates selection
    useEffect(() => {
        if (Array.isArray(selectedFocus) && selectedFocus.length > 0) {
            setFocusFieldError('');
        }
    }, [selectedFocus]);
    const [focusDropdownOpen, setFocusDropdownOpen] = useState(false);
    const focusDropdownRef = useRef(null);
    const [businessTypeDropdownOpen, setBusinessTypeDropdownOpen] = useState(false);
    const businessTypeDropdownRef = useRef(null);
    const [apparelMagicId, setApparelMagicId] = useState(initial?.apparelMagicId ?? null);

    // Load custom business types from Firestore (persisted)
    useEffect(() => {
        const fetchBusinessTypes = async () => {
            const typesRef = collection(db, collectionPath('businessType'));
            try {
                const snapshot = await getDocs(typesRef);
                const customTypes = snapshot.docs.map(d => d.id || d.data()?.name).filter(Boolean);
                setBusinessTypeOptions(prev => {
                    const merged = [...prev, ...customTypes.filter(t => !prev.includes(t))];
                    return merged;
                });
            } catch (err) {
                console.error('Failed to load business type', err);
                setError('Failed to load business type (permissions or network)');
            }
        };
        fetchBusinessTypes();
    }, []);

    // Load custom focus options from Firestore
    useEffect(() => {
        const fetchFocusOptions = async () => {
            const focusRef = collection(db, collectionPath('focusOptions'));
            try {
                const snapshot = await getDocs(focusRef);
                const customFocus = snapshot.docs.map(d => d.id || d.data()?.name).filter(Boolean);
                setFocusOptions(prev => {
                    const merged = [...prev, ...customFocus.filter(f => !prev.includes(f))];
                    return merged;
                });
                // clear any previously shown focus field error
                setFocusFieldError('');
            } catch (err) {
                console.error('Failed to load focus options', err);
                setError('Failed to load focus options (permissions or network)');
                setFocusFieldError('Failed to load focus options (permissions or network)');
            }
        };
        fetchFocusOptions();
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (focusDropdownRef.current && !focusDropdownRef.current.contains(event.target)) {
                setFocusDropdownOpen(false);
            }
        }
        if (focusDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [focusDropdownOpen]);

    // Close business type dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (businessTypeDropdownRef.current && !businessTypeDropdownRef.current.contains(event.target)) {
                setBusinessTypeDropdownOpen(false);
            }
        }
        if (businessTypeDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [businessTypeDropdownOpen]);

    const handleBusinessTypeChange = (e) => {
        const val = e.target.value;
        if (val === '__add_new__') {
            setShowNewBusinessType(true);
            setBusinessType('');
        } else {
            setShowNewBusinessType(false);
            setBusinessType(val);
        }
    };

    const handleFocusChange = (option) => {
        setSelectedFocus(prev => {
            const arr = Array.isArray(prev) ? prev : prev ? [prev] : [];
            return arr.includes(option)
                ? arr.filter(f => f !== option)
                : [...arr, option];
        });
    };

    const handleAddNewBusinessType = async () => {
        if (!newBusinessType.trim()) return;
        const typeId = newBusinessType.trim();
        try {
            await setDoc(doc(db, collectionPath('businessTypes'), typeId), { created: serverTimestamp(), createdBy: user?.uid || null });
            setBusinessTypeOptions(prev => [...prev, typeId]);
            setBusinessType(typeId);
            setShowNewBusinessType(false);
            setNewBusinessType('');
        } catch (err) {
            console.error('Failed to add business type', err);
            setError('Failed to add business type (permissions or network)');
        }
    };

    const handleAddNewFocus = async () => {
        if (!newFocus.trim()) return;
        const focusId = newFocus.trim();
        try {
            await setDoc(doc(db, collectionPath('focusOptions'), focusId), { created: serverTimestamp(), createdBy: user?.uid || null });
            setFocusOptions(prev => [...prev, focusId]);
            setSelectedFocus(prev => [...prev, focusId]);
            setShowNewFocus(false);
            setNewFocus('');
            setFocusFieldError('');
        } catch (err) {
            console.error('Failed to add focus option', err);
            setError('Failed to add focus option (permissions or network)');
            setFocusFieldError('Failed to add focus option (permissions or network)');
        }
    };

    // Update handleSubmit to validate required fields
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Required field validation
        if (!name.trim()) {
            setError('Customer Name is required');
            return;
        }
        if (!email.trim()) {
            setError('Email is required');
            return;
        }
        if (!phone.trim()) {
            setError('Phone is required');
            return;
        }
        if (!status.trim()) {
            setError('Status is required');
            return;
        }
        if (!salesRepId || !String(salesRepId).trim()) {
            setError('Assigned Sales Rep is required');
            return;
        }
        if (!businessType.trim()) {
            setError('Business Type is required');
            return;
        }
        if (!Array.isArray(selectedFocus) || selectedFocus.length === 0) {
            setError('Focus is required');
            setFocusFieldError('Focus is required');
            return;
        }

        setSaving(true);

        const canonicalName = sanitizeString(name, 200)?.trim() || '';
        
        // Get selected sales rep details for salesReps array
        const selectedRep = users.find(u => u.uid === salesRepId);
        const salesRepArray = selectedRep ? [{
            salesRepId: selectedRep.uid,
            salesRepName: selectedRep.displayName || `${selectedRep.firstName} ${selectedRep.lastName}`.trim()
        }] : [];
        
        const salesRepNames = salesRepArray.map(rep => rep.salesRepName).filter(Boolean);

        const data = {
            customerName: canonicalName,
            customerNameLower: canonicalName.toLowerCase(),
            customerEmail: sanitizeString(email, 200),
            customerPhone: sanitizeString(phone, 50),
            customerAddress1: sanitizeString(address1, 200),
            customerAddress2: sanitizeString(address2, 200),
            customerCity: sanitizeString(city, 100),
            customerState: sanitizeString(stateVal, 100),
            customerZip: sanitizeString(zip, 20),
            customerCountry: sanitizeString(country, 100),
            customerStatus: sanitizeString(status, 50),
            lastModified: lastModified ? convertDateForSave(lastModified) : serverTimestamp(),
            customerWebsite: sanitizeString(website, 200),
            businessFocus: (Array.isArray(selectedFocus) ? selectedFocus : []).map(v => sanitizeString(v, 50)).filter(Boolean),
            businessType: businessType ? [sanitizeString(businessType, 100)] : [],
            apparelMagicId: apparelMagicId ?? null,
            salesRepId: salesRepId || null,
            salesReps: salesRepArray,
            salesRepNames: salesRepNames,
            uid: user?.uid || null, // Owner user ID for permissions
            updatedAt: serverTimestamp(), // Auto-track when record was modified
        };
        
        // Add createdAt only for new customers
        if (!initial) {
            data.createdAt = serverTimestamp();
        }

        // Validate schema before saving
        const validation = validateEntity(data, 'customer');
        if (!validation.valid) {
            setError(validation.errors[0] || 'Invalid customer data');
            setSaving(false);
            return;
        }
        
        const perfId = PerformanceMonitor.startOperation(
            initial ? 'update-customer' : 'create-customer'
        );
        
        try {
            if (initial) {
                await setDoc(doc(db, collectionPath('customers'), initial.id), data, { merge: true });
                await logEntityChanges({
                    oldObj: initial,
                    newObj: { ...initial, ...data },
                    fields: [
                        'customerName', 'customerEmail', 'customerPhone', 'customerAddress1', 'customerAddress2', 'customerCity',
                        'customerState', 'customerZip', 'customerCountry', 'customerStatus', 'customerWebsite', 'businessFocus', 'businessType',
                    ],
                    entityType: 'Customer',
                    entityId: initial.id,
                    user,
                    action: 'Updated Customer'
                });
                // Gamification: Award points for customer update (with anti-abuse checks)
                try { 
                    await recordActivity('CUSTOMER_UPDATED', {
                        entityId: initial.id,
                        source: 'manual',
                        oldData: initial,
                        entityData: data,
                        fieldsToCheck: ['customerName', 'customerEmail', 'customerPhone', 'customerAddress1', 'customerCity', 'customerState', 'customerStatus', 'businessFocus', 'businessType']
                    }); 
                } catch (e) { console.warn('Gamification failed:', e); }
            } else {
                const docRef = await addDoc(collection(db, collectionPath('customers')), data);
                await setDoc(doc(db, collectionPath('customers'), docRef.id), { ...data, customerId: docRef.id }, { merge: true });
                await logUserAction({
                    userId: user.uid,
                    action: 'Added customer',
                    details: `Added customer "${data.customerName}" (${docRef.id})`
                });
                // Gamification: Award points for new customer (with anti-abuse validation)
                try { 
                    await recordActivity('CUSTOMER_CREATED', {
                        entityId: docRef.id,
                        source: 'manual',
                        entityData: { customerName: data.customerName }
                    }); 
                } catch (e) { console.warn('Gamification failed:', e); }
            }
            PerformanceMonitor.endOperation(perfId, true);
            onClose(true);
        } catch (err) {
            PerformanceMonitor.endOperation(perfId, false);
            const friendlyMsg = handleFirestoreError(
                err,
                initial ? 'update-customer' : 'create-customer',
                { customerName: data.customerName }
            );
            setError(friendlyMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!initial?.id) return;
        if (window.confirm('Are you sure you want to delete this customer?')) {
            setSaving(true);

            try {
                await deleteDoc(doc(db, collectionPath('customers'), initial.id));
                await logUserAction({
                    userId: user.uid,
                    action: 'Deleted customer',
                    details: `Deleted customer "${name}" (${initial.id})`
                });
                onClose();
            } catch (err) {
                setError('Failed to delete customer');
            } finally {
                setSaving(false);
            }
        }
    };

    // helper to render a user's display name (fallback to email/id)
    const formatUser = (u) => {
        if (!u) return '';
        const first = u.firstName || u.first_name || u.givenName || '';
        const last = u.lastName || u.last_name || u.familyName || '';
        const full = [first, last].filter(Boolean).join(' ');
        return full || u.name || u.displayName || u.email || u.uid || '';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <form
                onSubmit={handleSubmit}
                className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl space-y-4 max-h-[90vh] overflow-y-auto"
            >
                <h2 className="text-lg font-semibold">{initial ? 'Edit' : 'Add'} Customer</h2>
                {error && <p className="text-red-500">{error}</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name<span className="text-red-600">*</span></label>
                            <input
                                className="w-full border p-2 rounded"
                                placeholder="Customer Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                maxLength={100}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email<span className="text-red-600">*</span></label>
                            <SecureInput
                                className="w-full border p-2 rounded"
                                placeholder="Email"
                                type="email"
                                value={email}
                                onChange={setEmail}
                                maxLength={100}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
                            <input
                                className="w-full border p-2 rounded"
                                placeholder="https://example.com"
                                type="url"
                                value={website}
                                onChange={e => setWebsite(e.target.value)}
                                maxLength={200}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone<span className="text-red-600">*</span></label>
                            <input className="w-full border p-2 rounded" placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value)} required />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status<span className="text-red-600">*</span></label>
                            <select className="w-full border p-2 rounded" value={status} onChange={e => setStatus(e.target.value)} required>
                                <option value="">Select Status</option>
                                <option value="Lead">Lead</option>
                                <option value="Pending">Pending</option>
                                <option value="Approved">Approved</option>
                                <option value="Cancelled">Cancelled</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Sales Rep<span className="text-red-600">*</span></label>
                            <select
                                className="w-full border p-2 rounded"
                                value={salesRepId}
                                onChange={e => setSalesRepId(e.target.value)}
                                required
                            >
                                <option value="">Unassigned</option>
                                {getAssignmentUsers(users, initial?.id ? getInitialSalesRepId() : '').map(u => (
                                    <option key={u.uid || u.id} value={u.uid || u.id} disabled={u.role === 'disabled'}>
                                        {u.displayName || formatUser(u)}{u.role === 'disabled' ? ' (Deactivated)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* New: Customer ID (Apparel Magic) field */}
                                                <div className="mb-4">
                                                        <label className="block text-sm font-medium text-gray-700">Customer ID</label>
                                                        {hasPermission(user, FEATURE_PERMISSIONS.CUSTOMER_EDIT) ? (
                                                            <input
                                                                type="text"
                                                                className="border rounded px-3 py-2 w-full"
                                                                value={apparelMagicId ?? ''}
                                                                onChange={e => setApparelMagicId(e.target.value)}
                                                                placeholder="Enter Apparel Magic Customer ID or leave blank for CRM ID"
                                                            />
                                                        ) : (
                                                            <div className="border rounded px-3 py-2 w-full bg-gray-100 text-indigo-700">
                                                                {apparelMagicId
                                                                    ? apparelMagicId
                                                                    : (initial?.id || initial?.customerId || <span className="text-gray-400">(none)</span>)}
                                                            </div>
                                                        )}
                                                </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address 1</label>
                            <input className="w-full border p-2 rounded" placeholder="Address 1" value={address1} onChange={e => setAddress1(e.target.value)} />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address 2</label>
                            <input className="w-full border p-2 rounded" placeholder="Address 2" value={address2} onChange={e => setAddress2(e.target.value)} />
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                <input className="w-full border p-2 rounded" placeholder="City" value={city} onChange={e => setCity(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                <input className="w-full border p-2 rounded" placeholder="State" value={stateVal} onChange={e => setStateVal(e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                                <input className="w-full border p-2 rounded" placeholder="Zip" value={zip} onChange={e => setZip(e.target.value)} />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                            <input className="w-full border p-2 rounded" placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Business Type<span className="text-red-600">*</span></label>
                            <select
                                className="w-full border p-2 rounded"
                                value={showNewBusinessType ? '__add_new__' : (businessTypeOptions.includes(businessType) ? businessType : '')}
                                onChange={handleBusinessTypeChange}
                                required
                            >
                                <option value="">Select Business Type</option>
                                {businessTypeOptions.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                                <option value="__add_new__">Add New...</option>
                            </select>
                            {showNewBusinessType && (
                                <div className="mt-2 flex items-center space-x-2 w-full">
                                    <input
                                        type="text"
                                        className="border p-3 rounded min-w-0 w-full text-base"
                                        placeholder="Enter new business type"
                                        value={newBusinessType}
                                        onChange={e => setNewBusinessType(e.target.value)}
                                        autoFocus
                                    />
                                    <button
                                        type="button"
                                        className="px-4 py-2 bg-indigo-600 text-white rounded text-base"
                                        onClick={handleAddNewBusinessType}
                                    >
                                        Add
                                    </button>
                                    <button
                                        type="button"
                                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded text-base"
                                        onClick={() => {
                                            setShowNewBusinessType(false);
                                            setNewBusinessType('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Moved: Focus multiselect */}
                        <div>
                            <label className="block text-sm font-medium mb-1 mt-4">Business Focus<span className="text-red-600">*</span></label>
                            <div className="relative" ref={focusDropdownRef}>
                                {/* Invisible native select to trigger browser validation UI like Business Type */}
                                {/* Hidden select for form validation - removed 'required' since we validate in handleSubmit */}
                                <select
                                    aria-hidden="true"
                                    tabIndex={-1}
                                    aria-describedby={focusFieldError ? 'focus-error' : undefined}
                                    value={Array.isArray(selectedFocus) && selectedFocus.length > 0 ? selectedFocus[0] : ''}
                                    className="absolute inset-0 opacity-0 pointer-events-none transition-shadow duration-150 ease-in-out"
                                    onChange={() => { /* no-op: control mirrored by custom UI */ }}
                                >
                                    <option value="">Select Business Focus</option>
                                    {focusOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>

                                <button
                                    type="button"
                                    aria-describedby={focusFieldError ? 'focus-error' : undefined}
                                    aria-expanded={!!focusDropdownOpen}
                                    aria-controls="focus-listbox"
                                    aria-haspopup="listbox"
                                    className={`w-full text-left border p-2 rounded flex justify-between items-center ${Array.isArray(selectedFocus) } transition-shadow transition-colors duration-150 ease-in-out ${focusFieldError ? 'border-red-500 ring-2 ring-red-400 animate-pulse' : ''}`}
                                    onClick={() => setFocusDropdownOpen(open => !open)}
                                >
                                    <span className="truncate">
                                        {(Array.isArray(selectedFocus) && selectedFocus.length > 0)
                                            ? selectedFocus.join(', ')
                                            : 'Select Focus'}
                                    </span>
                                    <svg className="w-4 h-4 ml-2 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={focusDropdownOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                    </svg>
                                </button>

                                {focusDropdownOpen && (
                                    <div
                                        id="focus-listbox"
                                        role="listbox"
                                        aria-multiselectable="true"
                                        className="absolute z-40 mt-2 w-full bg-white border rounded shadow max-h-[60vh] overflow-auto p-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="space-y-1">
                                            {focusOptions.map(opt => (
                                                <label key={opt} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                                                    <input
                                                        type="checkbox"
                                                        checked={Array.isArray(selectedFocus) && selectedFocus.includes(opt)}
                                                        onChange={() => handleFocusChange(opt)}
                                                        className="form-checkbox"
                                                        aria-checked={Array.isArray(selectedFocus) && selectedFocus.includes(opt)}
                                                        role="option"
                                                        aria-selected={Array.isArray(selectedFocus) && selectedFocus.includes(opt)}
                                                    />
                                                    <span className="text-sm">{opt}</span>
                                                </label>
                                            ))}

                                            <div className="border-t mt-2 pt-2">
                                                {showNewFocus ? (
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="text"
                                                            className="border p-2 rounded flex-1"
                                                            placeholder="New focus (e.g., Pickleball)"
                                                            value={newFocus}
                                                            onChange={e => setNewFocus(e.target.value)}
                                                            autoFocus
                                                        />
                                                        <button
                                                            type="button"
                                                            className="px-3 py-1 bg-indigo-600 text-white rounded"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                const trimmed = (newFocus || '').trim();
                                                                if (!trimmed) return;
                                                                if (trimmed && !focusOptions.includes(trimmed)) {
                                                                    await handleAddNewFocus();
                                                                } else if (trimmed) {
                                                                    handleFocusChange(trimmed);
                                                                }
                                                                setShowNewFocus(false);
                                                                setNewFocus('');
                                                                setFocusDropdownOpen(true);
                                                            }}
                                                        >
                                                            Add
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="px-3 py-1 bg-gray-200 rounded"
                                                            onClick={(e) => { e.stopPropagation(); setShowNewFocus(false); setNewFocus(''); }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="w-full text-indigo-600 hover:underline text-sm"
                                                        onClick={() => { setShowNewFocus(true); setFocusDropdownOpen(true); }}
                                                    >
                                                        + Add New Focus
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {focusFieldError && <p id="focus-error" role="alert" aria-live="assertive" className="text-red-500 mt-1">{focusFieldError}</p>}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end space-x-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                    <button type="submit" disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded">{saving ? 'Saving...' : 'Save'}</button>
                </div>
            </form>
        </div>
    );
};

export default CustomerModal;


